import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import { useTranslation } from 'react-i18next'

interface MonitoringEmptyProps {
  /** Copy the public study link to the clipboard. */
  onCopyLink?: () => void
  /** Open the email-invite dialog. */
  onSendInvites?: () => void
  /** True briefly after a successful copy, to flip the button label. */
  copied?: boolean
}

/** Zero-respondents state: waiting-for-data hero + share actions + a tip. */
export default function MonitoringEmpty({ onCopyLink, onSendInvites, copied }: MonitoringEmptyProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('monitoring')

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '20px',
        borderStyle: 'dashed',
        borderWidth: 1.5,
        borderColor: c.ink32,
        p: { xs: 4, md: '60px' },
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: alpha(c.coral, 0.1),
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <HourglassEmptyOutlinedIcon sx={{ fontSize: 36, color: c.coral }} />
      </Box>

      <Stack spacing={1} sx={{ maxWidth: 480 }}>
        <Typography
          component="h2"
          sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 22, color: c.ink, lineHeight: 1.2 }}
        >
          {t('empty.title')}
        </Typography>
        <Typography sx={{ fontSize: 14.5, color: c.ink72, lineHeight: 1.55 }}>{t('empty.body')}</Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        <Button
          variant="contained"
          onClick={onCopyLink}
          startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{ borderRadius: 34, px: 2.5, py: 1, fontWeight: 700 }}
        >
          {copied ? t('header.copied') : t('empty.copyLink')}
        </Button>
        <Button
          variant="outlined"
          onClick={onSendInvites}
          startIcon={<IosShareOutlinedIcon sx={{ fontSize: 16, color: c.coral }} />}
          sx={{ borderRadius: 34, px: 2.5, py: 1, fontWeight: 700, borderColor: c.coral, color: c.coral }}
        >
          {t('empty.sendInvites')}
        </Button>
      </Stack>

      <Box
        sx={{
          mt: 1.5,
          px: 2.25,
          py: 1.5,
          bgcolor: c.gray50,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          maxWidth: 600,
        }}
      >
        <LightbulbOutlinedIcon sx={{ fontSize: 18, color: c.warning, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: c.ink72, lineHeight: 1.5, textAlign: 'left' }}>
          {t('empty.tip')}
        </Typography>
      </Box>
    </Paper>
  )
}
